/**
 * Coupon param utilities for preserving ?coupon= across internal navigation,
 * gated on a small allowlist of recognized trigger codes.
 *
 * Two-layer model (see docs/features/COUPON_PRICING.md):
 *   1. The TRIGGER (e.g. `sgn-promo`, `webinar-promo`) is what visitors see in
 *      URLs. It is hard-coded here and decoupled from Stripe. Persisted to
 *      localStorage under `landing_page_coupon` so the landing page's pricing
 *      variant survives internal nav / refresh / cross-tab.
 *   2. The Stripe promotion-code names (`NEXT_PUBLIC_COUPON_MONTHLY`,
 *      `NEXT_PUBLIC_COUPON_YEARLY`) live in env vars and are resolved at API
 *      time — they never appear in marketing URLs. The *resolved* Stripe code
 *      for the plan the visitor picked is persisted to localStorage under
 *      `landing_page_resolved_coupon` at Choose-Plan-click time so the React
 *      app's auth flow (login / checkout) can pick it up after the
 *      cross-tab signup → email-verify → login round-trip, where sessionStorage
 *      doesn't survive.
 *
 * Persistence rules:
 * - Trigger key (`landing_page_coupon`) accepts only known triggers — garbage
 *   URL params can't poison stored state.
 * - Resolved-code key (`landing_page_resolved_coupon`) is written only by
 *   `PricingContent.handleChoosePlan` after a successful trigger → Stripe code
 *   resolution, so it always holds a valid Stripe code when set.
 * - Both keys are cleared by the React app on successful sign-in
 *   (`AuthContext.tsx`) and at the start of checkout (`checkout.tsx`).
 */

import { config } from "./config";

const COUPON_STORAGE_KEY = "landing_page_coupon";
const RESOLVED_COUPON_STORAGE_KEY = "landing_page_resolved_coupon";
const PLAN_STORAGE_KEY = "landing_page_plan";

// Recognized trigger codes. Aliases for the same discount-variant behavior —
// marketing can pick which one fits a given campaign without code changes.
// Add new aliases here; lower-case is the storage normal form.
export const TRIGGER_CODES = ["sgn-promo", "webinar-promo"] as const;
export type CouponTrigger = (typeof TRIGGER_CODES)[number];

const TRIGGER_SET = new Set<string>(TRIGGER_CODES);

export function isCouponTrigger(value: string | null | undefined): value is CouponTrigger {
  if (!value) return false;
  return TRIGGER_SET.has(value.toLowerCase());
}

/**
 * Resolve a trigger to the env-mapped Stripe promotion-code name for the
 * given billing period. Returns null when the value isn't a recognized
 * trigger, or when the matching env var is empty.
 *
 * Use this at the moment of plan selection (checkout) — the Stripe code is
 * what flows to the backend's `coupon` param.
 */
export function resolveCouponForPlanType(
  trigger: string | null | undefined,
  planType: "monthly" | "yearly",
): string | null {
  if (!isCouponTrigger(trigger)) return null;
  const code = planType === "yearly" ? config.COUPON_YEARLY : config.COUPON_MONTHLY;
  return code ? code : null;
}

const subscribers = new Set<() => void>();

function notifyCouponChange(): void {
  subscribers.forEach((cb) => cb());
}

/**
 * Subscribe to coupon-store changes. Fires when this module mutates the keys
 * (same tab) or when another tab writes to localStorage (cross-tab via the
 * native `storage` event). Returns a cleanup function.
 *
 * Designed for use with `React.useSyncExternalStore`.
 */
export function subscribeToCouponStore(callback: () => void): () => void {
  subscribers.add(callback);
  if (typeof window !== "undefined") {
    window.addEventListener("storage", callback);
  }
  return () => {
    subscribers.delete(callback);
    if (typeof window !== "undefined") {
      window.removeEventListener("storage", callback);
    }
  };
}

/**
 * Get the coupon param value from the current URL. If it's a recognized
 * trigger, normalize to lower-case and persist; otherwise ignore. Returns
 * the persisted value (or null) so callers can react immediately.
 */
export function captureCouponFromUrl(): string | null {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  const hasCouponParam = params.has("coupon");
  const raw = params.get("coupon");
  if (!isCouponTrigger(raw)) {
    if (hasCouponParam) {
      try {
        localStorage.removeItem(COUPON_STORAGE_KEY);
        localStorage.removeItem(RESOLVED_COUPON_STORAGE_KEY);
        notifyCouponChange();
      } catch {
        // localStorage unavailable — ignore
      }
    }
    return null;
  }
  const normalized = raw!.toLowerCase();
  try {
    localStorage.setItem(COUPON_STORAGE_KEY, normalized);
    notifyCouponChange();
  } catch {
    // localStorage unavailable — ignore
  }
  return normalized;
}

/**
 * Read the stored or URL-supplied trigger. Returns null when nothing is set
 * or the value isn't a recognized trigger (defensive against stale storage
 * from the pre-redesign era when SGN20/SGN35 were used as URL params).
 */
export function getCoupon(): string | null {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  const fromUrl = params.get("coupon");
  if (params.has("coupon")) {
    return isCouponTrigger(fromUrl) ? fromUrl!.toLowerCase() : null;
  }
  try {
    const stored = localStorage.getItem(COUPON_STORAGE_KEY);
    if (isCouponTrigger(stored)) return stored.toLowerCase();
  } catch {
    // localStorage unavailable
  }
  return null;
}

/**
 * Persist the *resolved* Stripe promotion-code name (not the trigger) so the
 * React app's auth flow can fall back to it when sessionStorage doesn't
 * survive a cross-tab signup → email-verify round-trip. Pass null to clear.
 *
 * Only called by `PricingContent.handleChoosePlan` after
 * `resolveCouponForPlanType` succeeds. The trigger itself stays in
 * `landing_page_coupon`; this key is a parallel signal for the post-signup
 * checkout step that needs the Stripe code, not the trigger.
 */
export function setResolvedCoupon(stripeCode: string | null): void {
  if (typeof window === "undefined") return;
  try {
    if (stripeCode) {
      localStorage.setItem(RESOLVED_COUPON_STORAGE_KEY, stripeCode);
    } else {
      localStorage.removeItem(RESOLVED_COUPON_STORAGE_KEY);
    }
    notifyCouponChange();
  } catch {
    // localStorage unavailable — ignore
  }
}

/**
 * Get the stored plan lookup key.
 */
export function getPlan(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return localStorage.getItem(PLAN_STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * Store the plan lookup key.
 */
export function setPlan(plan: string): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(PLAN_STORAGE_KEY, plan);
    notifyCouponChange();
  } catch {
    // ignore
  }
}
